/**
 * Public account-activation (set-password) API client (PRD-33, ADR-0030).
 *
 * The password-setup endpoints are unauthenticated (auth=None on the
 * backend). We use a SEPARATE axios instance — NOT the standard admin
 * ``apiClient`` — so the admin JWT from a logged-in console session is
 * never attached to these public requests, and the client's 401-refresh
 * interceptor (which redirects to /login) doesn't fire on a public 4xx.
 * This mirrors the professor app's account-activation feature and the
 * threat model from ADR-0030.
 */

import axios, { type AxiosError } from "axios"

import { env } from "@/env"

const setupClient = axios.create({
  baseURL: `${env.VITE_API_BASE_URL}/api`,
  timeout: 30_000,
})

/** Sanitized GET context — mirrors backend ``PasswordSetupContextOut``. */
export interface SetupTokenContext {
  status: "active" | string
  user_type: "professor" | "admin" | string
  full_name: string
  scope_name: string
  /** The scope's crest (public branding URL), when it has one. */
  scope_logo_url?: string | null
  email_masked: string
}

/** POST success — mirrors backend ``set_password`` response. */
export interface SetPasswordSuccess {
  status: "ok"
  user_type: "professor" | "admin" | string
}

export interface SetPasswordPayload {
  new_password: string
  confirm_password: string
}

interface SetupErrorPayload {
  detail: string
}

/** Pull the backend ``{ detail: "<code>" }`` error code off an axios error. */
export function setupErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError<SetupErrorPayload>
    return axiosErr.response?.data?.detail ?? null
  }
  return null
}

/** POST /api/public/password-reset/request response — always 200 + ok on a
 *  valid request (no account enumeration). */
export interface PasswordResetRequestSuccess {
  status: "ok"
}

export const accountActivationApi = {
  getContext: (token: string) =>
    setupClient
      .get<SetupTokenContext>(`/public/password-setup/${encodeURIComponent(token)}`)
      .then((r) => r.data),
  setPassword: (token: string, payload: SetPasswordPayload) =>
    setupClient
      .post<SetPasswordSuccess>(`/public/password-setup/${encodeURIComponent(token)}`, payload)
      .then((r) => r.data),
  /**
   * Request a password-reset email for an admin account. The backend ALWAYS
   * returns 200 `{ status: "ok" }` regardless of whether the account exists
   * (no enumeration); a 429 `setup_ip_rate_limited` is the only error the
   * caller needs to surface distinctly.
   */
  requestPasswordReset: (email: string) =>
    setupClient
      .post<PasswordResetRequestSuccess>("/public/password-reset/request", {
        email,
        user_type: "admin",
      })
      .then((r) => r.data),
}
